// src/components/status/StatusBar.jsx
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { alpha, styled } from '@mui/material/styles';
import Paper from '@mui/material/Paper';
import { Chip, Tooltip } from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import WifiIcon from '@mui/icons-material/Wifi';
import WifiOffIcon from '@mui/icons-material/WifiOff';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.tz.setDefault('America/Porto_Velho');

const Dock = styled(Paper)(({ theme, ownerState }) => ({
  position: 'fixed',
  right: 16,
  bottom: ownerState.offsetBottom ?? 16,
  display: 'flex',
  gap: 6,
  flexWrap: 'wrap',
  alignItems: 'center',
  padding: 6,
  borderRadius: 14,
  background: alpha(theme.palette.common.black, 0.35),
  border: `1px solid ${alpha(theme.palette.common.white, 0.08)}`,
  backdropFilter: 'blur(8px)',
  boxShadow: '0 12px 32px rgba(0,0,0,.35)',
  zIndex: 1500,
}));

const Pill = styled(Chip)(({ theme, ownerState }) => {
  const base = ownerState.base || theme.palette.info.main;
  return {
    height: 26,
    borderRadius: 14,
    fontSize: 12,
    fontWeight: 600,
    letterSpacing: 0.15,
    color: alpha(base, 0.95),
    backgroundColor: alpha(base, 0.12),
    border: `1px solid ${alpha(base, 0.35)}`,
    '& .MuiChip-icon': { color: alpha(base, 0.9) },
  };
});

function usePoll(url, intervalMs) {
  const [state, setState] = useState({ loading: true, error: null, data: null });
  const lastJsonRef = useRef(null);

  useEffect(() => {
    const base = (process.env.REACT_APP_API || '').replace(/\/$/, '');
    const full = `${base}${url}`;
    let timer;
    let first = true;

    const run = async () => {
      try {
        const res = await fetch(full, { method: 'GET', cache: 'no-store' });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const json = await res.json();

        // evita re-render sem necessidade
        const key = JSON.stringify(json);
        if (key !== lastJsonRef.current) {
          lastJsonRef.current = key;
          setState({ loading: false, error: null, data: json });
        } else if (first) {
          setState({ loading: false, error: null, data: json });
        }
      } catch (e) {
        setState(prev => ({ loading: false, error: e, data: prev.data }));
      } finally {
        first = false;
        timer = setTimeout(run, intervalMs);
      }
    };

    run();
    return () => clearTimeout(timer);
  }, [url, intervalMs]);

  return state;
}

export default function StatusBar({ intervalMs = 60_000, offsetBottom = 16 }) {
  const ping = usePoll('/health/ping', intervalMs);
  const blip = usePoll('/health/blip', intervalMs);

  // API
  const apiUp = !ping.error;
  const apiPill = apiUp
    ? { label: 'Conexão', icon: <WifiIcon sx={{ fontSize: 18 }} />, base: '#32D583' }
    : { label: 'Sem conexão', icon: <WifiOffIcon sx={{ fontSize: 18 }} />, base: '#FDB022' };

  const blipLoading = blip.loading;
  const status = blip.data?.status;        // 'up' | 'down'
  const inWindow = !!blip.data?.in_window; // true | false
  const lastMs = blip.data?.last_event_ms ?? null;

  const blipPill = useMemo(() => {
    if (blipLoading) {
      return { label: 'Serviços Blip: verificando…', icon: <HourglassEmptyIcon sx={{ fontSize: 18 }} />, base: '#7CD4E7' };
    }
    if (status === 'down' && inWindow) {
      return { label: 'Serviços Blip: instável', icon: <ErrorOutlineIcon sx={{ fontSize: 18 }} />, base: '#F97066' };
    }
    if (!inWindow) {
      return { label: 'Serviços Blip: fora do horário de monitoramento', icon: <AccessTimeIcon sx={{ fontSize: 18 }} />, base: '#94A3B8' };
    }
    return { label: 'Serviços Blip: operante', icon: <CheckCircleOutlineIcon sx={{ fontSize: 18 }} />, base: '#22D3EE' };
  }, [blipLoading, status, inWindow]);

  const lastEventText = useMemo(() => {
    if (!lastMs) return 'Sem eventos recentes da Blip nesta janela.';
    const when = dayjs(lastMs).tz('America/Porto_Velho');
    return `Último evento: ${when.format('DD/MM/YYYY HH:mm:ss')} (PV-RO)`;
  }, [lastMs]);

  return (
    <Dock elevation={0} ownerState={{ offsetBottom }}>
      <Pill size="small" variant="outlined" icon={apiPill.icon} label={apiPill.label} ownerState={{ base: apiPill.base }} />
      <Tooltip title={lastEventText} arrow>
        <Pill size="small" variant="outlined" icon={blipPill.icon} label={blipPill.label} ownerState={{ base: blipPill.base }} />
      </Tooltip>
    </Dock>
  );
}
